import {
  AddProduktToAngebotAction,
  BeginnAblaufGeaendert,
  CommonInsuranceData,
  GlobalActions,
  isMaklerbetreuerOrMaklerOrUvm,
  VertragslaufzeitChanged,
  VorbelegungPayload,
  ZahlungsoptionenChanged,
} from '@ps20-frontend/shop-integration-redux';
import {
  AktionsNachlass,
  App,
  areAppTypesEquivalent,
  BaseAppState,
  Vertragsoptionen,
} from '@ps20-frontend/frontend-models';
import { AnyAction } from 'redux';
import { EinstiegResponse, Zahlungsart } from '@ps20/bff-models';
import dayjs from 'dayjs';
import { DATEFORMAT_LOCAL_STATE } from '@ps20-frontend/utils';

function vertragslaufzeitChanged(
  sectionState: Vertragsoptionen,
  action: AnyAction,
  appId: App,
  state: BaseAppState,
): Vertragsoptionen {
  const actionPayload: VertragslaufzeitChanged = action.payload;
  if (actionPayload.appId === appId && actionPayload.angebotsId === state.angebotsId) {
    return {
      ...sectionState,
      vertragslaufzeit: actionPayload.vertragslaufzeit,
      ausgewaehltevertragslaufzeit: actionPayload.vertragslaufzeit,
    };
  }
  return sectionState;
}

function updateZahlungsoptionen(
  sectionState: Vertragsoptionen,
  action: AnyAction,
): Vertragsoptionen {
  const zahlungsoptionenChanged: ZahlungsoptionenChanged = action.payload;
  return {
    ...sectionState,
    zahlungsweise: zahlungsoptionenChanged.zahlungsweise
      ? zahlungsoptionenChanged.zahlungsweise
      : sectionState.zahlungsweise,
    zahlungsart: zahlungsoptionenChanged.zahlungsart
      ? zahlungsoptionenChanged.zahlungsart
      : sectionState.zahlungsart,
  };
}

function einstiegRequestSuccessful(
  sectionState: Vertragsoptionen,
  action: AnyAction,
): Vertragsoptionen {
  const einstiegResponse: EinstiegResponse = action.payload;
  const vertreter = einstiegResponse.vertreter;
  return {
    ...sectionState,
    zahlungsart:
      vertreter.isInkasso && isMaklerbetreuerOrMaklerOrUvm(vertreter.role)
        ? Zahlungsart.MAKLER_INKASSO
        : sectionState.zahlungsart,
  };
}

function updateAktionsnachlassCode(
  sectionState: Vertragsoptionen,
  action: AnyAction,
  state: BaseAppState,
): Vertragsoptionen {
  const actionPayload: AktionsNachlass = action.payload;
  if (isValidNachlassCode(state, actionPayload.code)) {
    return {
      ...sectionState,
      selectedKooperation: actionPayload.code,
    };
  }
  return sectionState;
}

function checkValidAktionsnachlassCode(
  sectionState: Vertragsoptionen,
  state: BaseAppState,
): Vertragsoptionen {
  if (!isValidNachlassCode(state, sectionState.selectedKooperation)) {
    return {
      ...sectionState,
      selectedKooperation: null,
    };
  } else {
    return sectionState;
  }
}

function updateBeginnAblaufOfProdukte(
  sectionState: Vertragsoptionen,
  action: AnyAction,
  appId: App,
  state: BaseAppState,
): Vertragsoptionen {
  const actionPayload: BeginnAblaufGeaendert[] = action.payload;
  const matchingUpdate = actionPayload.find(
    ({ appId: actionPayloadAppId, angebotsId }) =>
      appId === actionPayloadAppId && angebotsId === state.angebotsId,
  );
  if (matchingUpdate) {
    const newState = sectionState.beibehalten
      ? sectionState
      : {
          ...sectionState,
          beginnDatum: matchingUpdate.beginn,
          ablaufDatum: matchingUpdate.ablauf,
        };
    return { ...newState, aenderungdatum: matchingUpdate.beginn };
  }
  return sectionState;
}

function vorbelegungRequested(
  sectionState: Vertragsoptionen,
  action: AnyAction,
  appId: App,
  state: BaseAppState,
): Vertragsoptionen {
  const actionPayload: VorbelegungPayload = action.payload;
  if (
    actionPayload.appId === appId &&
    actionPayload.angebotsId === state.angebotsId &&
    actionPayload.vorhandeneVorbelegungdaten
  ) {
    return {
      ...sectionState,
      kurzbezeichnung: actionPayload.vorhandeneVorbelegungdaten.kurzbezeichnung,
      ...(actionPayload.vorhandeneVorbelegungdaten.selbstbeteiligungOriginal != null && {
        selbstbeteiligung: actionPayload.vorhandeneVorbelegungdaten.selbstbeteiligungOriginal,
      }),
    };
  }
  return sectionState;
}

function addProduktToAngebote(
  sectionState: Vertragsoptionen,
  action: AnyAction,
  appId: App,
  state: BaseAppState,
): Vertragsoptionen {
  const addProduktAction: AddProduktToAngebotAction = action.payload;
  const currentDateObj = dayjs();
  const beginnDateObj = dayjs(sectionState.beginnDatum, DATEFORMAT_LOCAL_STATE);
  const aenderungDateObj = dayjs(sectionState.aenderungdatum, DATEFORMAT_LOCAL_STATE);

  if (
    areAppTypesEquivalent(addProduktAction.appId, appId) &&
    addProduktAction.angebotsId === state.angebotsId
  ) {
    return {
      ...sectionState,
      angebotsdatum: !sectionState.angebotsdatum
        ? addProduktAction.angebotsdatum
        : sectionState.angebotsdatum,
      beginnDatum:
        !sectionState.beibehalten && currentDateObj.isAfter(beginnDateObj)
          ? currentDateObj.add(1, 'day').format(DATEFORMAT_LOCAL_STATE)
          : sectionState.beginnDatum,
      aenderungdatum:
        !sectionState.beibehalten && currentDateObj.isAfter(aenderungDateObj)
          ? currentDateObj.add(1, 'day').format(DATEFORMAT_LOCAL_STATE)
          : sectionState.aenderungdatum,
    };
  }
  return sectionState;
}

export const vertragsoptionenReducer = function (
  sectionState: Vertragsoptionen = null,
  action: AnyAction,
  appId: App,
  state: BaseAppState,
): Vertragsoptionen {
  switch (action.type) {
    case GlobalActions.VERTRAGSLAUFZEIT_CHANGED:
      return vertragslaufzeitChanged(sectionState, action, appId, state);
    case GlobalActions.ZAHLUNGSOPTIONEN_CHANGED:
    case GlobalActions.ZAHLUNGSOPTIONEN_CHANGED_VORVERTRAG:
      return updateZahlungsoptionen(sectionState, action);
    case GlobalActions.EINSTIEG_REQUEST_SUCCESSFUL:
      return einstiegRequestSuccessful(sectionState, action);
    case GlobalActions.COMMON_INSURANCE_DATA_RECEIVED: {
      const commonInsuranceData: CommonInsuranceData = action.payload;
      return {
        ...sectionState,
        zahlungsart: commonInsuranceData.vertragsoptionen.zahlungsart,
        zahlungsweise: commonInsuranceData.vertragsoptionen.zahlungsweise,
        selectedKooperation: commonInsuranceData.vertragsoptionen.selectedKooperation,
      };
    }
    case GlobalActions.UPDATE_AKTIONSNACHLASS_CODE:
      return updateAktionsnachlassCode(sectionState, action, state);
    case GlobalActions.CHECK_VALID_AKTIONSNACHLASS_CODE:
      return checkValidAktionsnachlassCode(sectionState, state);
    case GlobalActions.UPDATE_BEGINN_ABLAUF_OF_PRODUKTE:
      return updateBeginnAblaufOfProdukte(sectionState, action, appId, state);
    case GlobalActions.VORBELEGUNG_REQUESTED:
      return vorbelegungRequested(sectionState, action, appId, state);
    case GlobalActions.ADD_PRODUKT_TO_ANGEBOTE:
      return addProduktToAngebote(sectionState, action, appId, state);
    default:
      return sectionState;
  }
};

export const isValidNachlassCode = (state: BaseAppState, nachlassCodeName: string): boolean => {
  return (
    state.transientState.kooperationen?.map(value => value?.code).includes(nachlassCodeName) ||
    !nachlassCodeName
  );
};
